/**
 * Generic progress info for LCAoM operations.
 * Used by various LCAoMService methods that process large datasets.
 */
export interface TaskProgress {
  /** Unique identifier for the task type */
  taskId: string;
  /** Human-readable label for the current phase */
  label: string;
  /** Current phase of the operation */
  phase: 'preparing' | 'processing' | 'finalizing' | 'complete';
  /** Number of items processed so far */
  processed: number;
  /** Total number of items to process */
  total: number;
  /** Progress percentage (0-100) */
  percent: number;
  /** Optional sub-label for additional context */
  subLabel?: string;
}

/**
 * Task IDs for different LCAoM operations.
 */
export const TASK_IDS = {
  ASSIGN_MATERIALS: 'assignMaterials',
  REMOVE_MATERIALS: 'removeMaterials',
  AUTO_ASSIGN: 'autoAssign',
  CALCULATE_TOTALS: 'calculateTotals',
  EXPORT_DATA: 'exportData',
} as const;

export type TaskId = typeof TASK_IDS[keyof typeof TASK_IDS];

/**
 * Callback type for progress reporting.
 */
export type ProgressCallback = (progress: TaskProgress) => void;

/**
 * Helper to create a progress object.
 */
export function createProgress(
  taskId: string,
  label: string,
  phase: TaskProgress['phase'],
  processed: number,
  total: number,
  subLabel?: string
): TaskProgress {
  return {
    taskId,
    label,
    phase,
    processed,
    total,
    percent: total > 0 ? Math.round((processed / total) * 100) : 0,
    subLabel,
  };
}
